import { NavigationEnd, Router } from '@angular/router'
import { Observable, filter, map } from 'rxjs'

/**
 *  Util methods to extract information from Angular route navigation
 */
export class RouterUtils {
  /**
   * Extract previous navigation parameter value
   * @param router
   * @param paramName
   */
  public static getPreviousNavigationParam(router: Router, paramName: string): string | null | undefined {
    return router.getCurrentNavigation()?.previousNavigation?.finalUrl?.queryParamMap.get(paramName)
  }

  /**
   * Extract last part of previous navigation URL.
   * For example, '/summer/categories/123' returns '123'
   * @param router
   */
  public static getPreviousNavigationChild(router: Router): string | null | undefined {
    const url = router.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString()

    return url?.split('/').pop()?.split('?')[0]
  }

  /**
   * Extract the last part or last two parts of the current navigation URL.
   * For example, '/summer/categories/123' returns 'categories/123'
   * and '/summer/items' returns 'items'.
   * @param router
   */
  public static getCurrentRoute(router: Router): Observable<string | undefined> {
    return router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => {
        const urlSegments = router.url.split('?')[0].split('/')
        urlSegments.shift() // remove auction url segment
        return urlSegments.length > 2 ? urlSegments.slice(-2).join('/') : urlSegments.pop()
      }),
    )
  }

  /**
   * Reloads the current route. Useful for when there's a need to
   * retrigger the API requests associated with the current route,
   * as it forces Angular to destroy and recreate the component/s.
   * @param router
   */
  public static reloadCurrentRoute(router: Router): void {
    router.navigateByUrl(router.routerState.snapshot.url, {
      state: { shouldReuseRoute: false },
    })
  }

  /**
   * Checks if the current route has the passed query parameter
   * @param router
   */
  public static hasQueryParam(router: Router, paramName: string): boolean {
    return new URLSearchParams(router.url.split('?')[1]).has(paramName)
  }
}
